/**
 * Print Utilities for Bill Printing
 * Supports browser print, PDF generation, and direct printer connection guidance
 */

import logoImage from '../components/assets/battasesukhsagarlogo.png';

/**
 * Convert image to base64 data URL
 * @param {string} imagePath - Path to the image
 * @returns {Promise<string>} Base64 data URL
 */
const imageToBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      try {
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

/**
 * Print bill using browser's native print dialog
 * @param {Object} billData - The bill data object
 */
export const printBill = async (billData) => {
  if (!billData) {
    console.error('No bill data provided for printing');
    return;
  }

  // Convert logo to base64
  let logoBase64 = '';
  try {
    logoBase64 = await imageToBase64(logoImage);
  } catch (error) {
    console.warn('Could not load logo image:', error);
  }

  // Convert QR code to base64
  let qrCodeBase64 = '';
  try {
    const qrCodePath = require('../components/assets/paymentqr.jpeg');
    qrCodeBase64 = await imageToBase64(qrCodePath);
  } catch (error) {
    console.warn('Could not load QR code image:', error);
  }

  // Create a new window for printing
  const printWindow = window.open('', '_blank');

  // Create HTML content matching PDF format
  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Bill-${billData.billNumber || 'N/A'}</title>
        <style>
          @media print {
            @page {
              size: 80mm auto; /* Thermal printer width */
              margin: 0;
            }
            body {
              margin: 0;
              padding: 10px;
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              width: 80mm;
            }
            * {
              box-sizing: border-box;
            }
          }
          @media screen {
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              padding: 20px;
              max-width: 300px;
              margin: 0 auto;
            }
          }
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 10px;
            margin-bottom: 10px;
          }
          .restaurant-logo {
            max-width: 60px;
            max-height: 60px;
            margin: 0 auto 10px;
            display: block;
          }
          .restaurant-name {
            font-size: 16px;
            font-weight: bold;
            margin: 0 0 5px 0;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .restaurant-info {
            font-size: 10px;
            margin: 3px 0;
            color: #333;
          }
          .divider {
            border: none;
            border-top: 1px dashed #000;
            margin: 10px 0;
          }
          .details {
            margin-bottom: 10px;
            font-size: 11px;
          }
          .detail-row {
            display: flex;
            justify-content: space-between;
            margin: 3px 0;
          }
          .items {
            margin: 10px 0;
          }
          .item-row {
            display: flex;
            justify-content: space-between;
            margin: 5px 0;
            font-size: 11px;
          }
          .item-name {
            flex: 1;
            margin-right: 10px;
          }
          .item-details {
            display: flex;
            justify-content: space-between;
            width: 100%;
          }
          .item-qty {
            margin-right: 10px;
          }
          .totals {
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px dashed #000;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 5px 0;
            font-size: 11px;
          }
          .total-row.highlight {
            font-size: 13px;
            font-weight: bold;
          }
          .footer {
            text-align: center;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px dashed #000;
            font-size: 10px;
          }
          .qr-code {
            max-width: 60px;
            max-height: 60px;
            margin: 10px auto;
            display: block;
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${logoBase64 ? `<img src="${logoBase64}" alt="Restaurant Logo" class="restaurant-logo" />` : ''}
          <h2 class="restaurant-name">HOTEL BATTASE SUKHSAGAR</h2>
          <div class="restaurant-info">Vani-saptashrungi gad Road</div>
          <div class="restaurant-info">Vani,Nashik</div>
          <div class="restaurant-info">Phone: +91-9011478282</div>
        </div>

        <hr class="divider">

        <div class="details">
          <div class="detail-row">
            <span>Bill No:</span>
            <span>${billData.billNumber || 'N/A'}</span>
          </div>
          <div class="detail-row">
            <span>Date:</span>
            <span>${billData.date || new Date().toLocaleDateString()}</span>
          </div>
          <div class="detail-row">
            <span>Time:</span>
            <span>${billData.time || new Date().toLocaleTimeString()}</span>
          </div>
          <div class="detail-row">
            <span>Table:</span>
            <span>${billData.tableNumber}</span>
          </div>
          <div class="detail-row">
            <span>Waiter:</span>
            <span>${billData.waiterName}</span>
          </div>
        </div>

        <hr class="divider">

        <div class="items">
          ${(billData.items || []).map(item => `
            <div class="item-row">
              <span class="item-name">${item.name || item.itemName || 'Item'}</span>
              <div class="item-details">
                <span class="item-qty">${item.quantity}</span>
                <span>₹${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            </div>
          `).join('')}
        </div>

        <hr class="divider">

        <div class="totals">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>₹${billData.subtotal.toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>GST (5%):</span>
            <span>₹${billData.gst.toFixed(2)}</span>
          </div>
          ${billData.acHallCharge > 0 ? `
            <div class="total-row">
              <span>AC Hall (20%):</span>
              <span>₹${billData.acHallCharge.toFixed(2)}</span>
            </div>
          ` : ''}
          ${billData.discount > 0 ? `
            <div class="total-row">
              <span>Discount:</span>
              <span>-₹${billData.discount.toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="total-row highlight">
            <span>Total:</span>
            <span>₹${billData.total.toFixed(2)}</span>
          </div>
        </div>

        <div class="footer">
          <div style="font-weight: bold; margin-bottom: 10px;">Pay Bill</div>
          ${qrCodeBase64 ? `<img src="${qrCodeBase64}" alt="Payment QR Code" class="qr-code" />` : ''}
          <div>Thank you for visiting</div>
          <div>Visit Again</div>
          <div style="margin-top: 5px; font-size: 9px;">Visit our website:</div>
          <div style="font-style: italic;">www.hotelbattasesukhsagar.in</div>
          <div style="margin-top: 5px; font-size: 8px; color: #666;">Powered By www.artifycode.com</div>
        </div>
      </body>
    </html>
  `;

  printWindow.document.write(printContent);
  printWindow.document.close();

  // Wait for content to load, then print
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
    // Optionally close the window after printing
    // printWindow.close();
  }, 250);
};

/**
 * Generate PDF from bill data
 * @param {Object} billData - The bill data object
 * @param {string} filename - Optional filename for the PDF
 */
export const generatePDF = async (billData, filename = 'bill.pdf') => {
  try {
    // Dynamic import of jsPDF (install it: npm install jspdf)
    let jsPDF;
    try {
      // Dynamic import of jsPDF - this will show a webpack warning if jspdf is not installed
      // but the code will handle it gracefully at runtime
      const jsPDFModule = await import('jspdf');
      if (jsPDFModule?.jsPDF) {
        jsPDF = jsPDFModule.jsPDF;
      } else {
        throw new Error('jsPDF not available');
      }
    } catch (importError) {
      // If jsPDF is not installed, show helpful message
      console.warn('jsPDF library not found. PDF generation requires jsPDF library.');
      alert('PDF generation requires jsPDF library.\n\nPlease install it by running:\nnpm install jspdf\n\nFor now, you can use the Print button to print the bill.');
      return;
    }
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [80, 200] // Thermal printer size
    });

    // Set font
    doc.setFont('courier', 'normal');
    doc.setFontSize(10);

    let y = 10;

    // Add logo
    try {
      const logoBase64 = await imageToBase64(logoImage);
      // Add logo image (15mm width, auto height, centered)
      const logoWidth = 20;
      const logoHeight = (logoWidth * 0.75); // Maintain aspect ratio (adjust as needed)
      doc.addImage(logoBase64, 'PNG', (80 - logoWidth) / 2, y, logoWidth, logoHeight);
      y += logoHeight + 3;
    } catch (error) {
      console.warn('Could not load logo image for PDF:', error);
    }

    // Header
    doc.setFontSize(12);
    doc.setFont('courier', 'bold');
    doc.text('HOTEL BATTASE SUKHSAGAR', 40, y, { align: 'center' });
    y += 5;
    doc.setFontSize(8);
    doc.setFont('courier', 'normal');
    doc.text('Vani-saptashrungi gad Road', 40, y, { align: 'center' });
    y += 3;
    doc.text('Vani,Nashik', 40, y, { align: 'center' });
    y += 3;
    doc.text('Phone: +91-9011478282', 40, y, { align: 'center' });
    y += 5;
    doc.line(5, y, 75, y);
    y += 5;

    // Bill details
    doc.setFontSize(9);
    doc.text(`Bill No: ${billData.billNumber || 'N/A'}`, 5, y);
    y += 4;
    doc.text(`Date: ${billData.date || new Date().toLocaleDateString()}`, 5, y);
    y += 4;
    doc.text(`Time: ${billData.time || new Date().toLocaleTimeString()}`, 5, y);
    y += 4;
    doc.text(`Table: ${billData.tableNumber}`, 5, y);
    y += 4;
    doc.text(`Waiter: ${billData.waiterName}`, 5, y);
    y += 5;
    doc.line(5, y, 75, y);
    y += 5;

    // Items Table Header
    doc.setFontSize(8);
    doc.setFont('courier', 'bold');
    const leftMargin = 5; // consistent left margin for all content
    const itemX = leftMargin;   // item starts at left margin
    const qtyX = 38;            // more space for item
    const priceX = 75;          // right margin, inside 80mm page
    doc.text('Item', itemX, y);
    doc.text('Qty', qtyX, y, { align: 'center' });
    doc.text('Price', priceX, y, { align: 'right' });
    y += 4;
    doc.setFont('courier', 'normal');
    // Items Rows
    billData.items.forEach((item) => {
      const itemName = item.itemName || item.name || 'Item';
      const qty = String(item.quantity);
      const price = `${(item.price * item.quantity).toFixed(2)}`;
      doc.text(itemName, itemX, y);
      doc.text(qty, qtyX, y, { align: 'center' });
      doc.text(price, priceX, y, { align: 'right' });
      y += 4;
    });

    y += 2;
    doc.line(5, y, 75, y);
    y += 4;

    // Totals Section
    doc.setFont('courier', 'normal');
    doc.setFontSize(9);
    doc.text('Subtotal', 5, y);
    doc.text(`${billData.subtotal.toFixed(2)}`, 75, y, { align: 'right' });
    y += 4;
    doc.text('GST (5%)', 5, y);
    doc.text(`${billData.gst.toFixed(2)}`, 75, y, { align: 'right' });
    y += 4;
    if (billData.discount > 0) {
      doc.text('Discount', 5, y);
      doc.text(`-${billData.discount.toFixed(2)}`, 75, y, { align: 'right' });
      y += 4;
    }
    doc.setFont('courier', 'bold');
    doc.setFontSize(10);
    doc.text('Total', 5, y);
    doc.text(`${billData.total.toFixed(2)}`, 75, y, { align: 'right' });
    y += 5;

    doc.line(5, y, 75, y);
    y += 5;

    // Footer layout after Total
    doc.setFont('courier', 'bold');
    doc.setFontSize(9);
    doc.text('Pay Bill', 40, y, { align: 'center' });
    y += 4;
    // Add QR code for payment
    try {
      const qrCodePath = require('../components/assets/paymentqr.jpeg');
      const qrCodeBase64 = await imageToBase64(qrCodePath);
      // Center QR code (30mm width)
      const qrWidth = 30;
      const qrHeight = 30;
      doc.addImage(qrCodeBase64, 'JPEG', (80 - qrWidth) / 2, y, qrWidth, qrHeight);
      y += qrHeight + 8; // Extra margin after scanner
    } catch (err) {
      console.warn('QR code image not found or failed to load:', err);
    }
    doc.setFont('courier', 'normal');
    doc.setFontSize(8);
    doc.text('Thank you for visiting', 40, y, { align: 'center' });
    y += 4;
    doc.text('Visit Again', 40, y, { align: 'center' });
    y += 4;
    doc.setFontSize(7);
    doc.text('Visit our website:', 40, y, { align: 'center' });
    y += 3;
    doc.setFont('times', 'italic'); // Use another font for website
    doc.setFontSize(8);
    doc.text('www.hotelbattasesukhsagar.in', 40, y, { align: 'center' });
    y += 4;
    doc.setFont('courier', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0); // Black color
    doc.text('Powered By www.artifycode.com', 40, y, { align: 'center' });
    // Save the PDF
    doc.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    // Fallback to browser print if jsPDF is not available
    alert('PDF generation requires jsPDF library. Please install it: npm install jspdf');
  }
};

/**
 * Print directly to a connected printer (requires backend support)
 * This is a placeholder function - actual implementation requires server-side code
 * @param {Object} billData - The bill data object
 * @param {string} printerName - Optional printer name
 */
export const printToPrinter = async (billData, printerName = null) => {
  try {
    // This would typically make an API call to your backend
    // which handles direct printer communication
    const response = await fetch('/api/print', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        billData,
        printerName,
      }),
    });

    if (!response.ok) {
      throw new Error('Print request failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Error printing to printer:', error);
    // Fallback to browser print
    alert('Direct printer connection not available. Using browser print instead.');
    return null;
  }
};

/**
 * Format bill data for printing
 * @param {Object} order - The order object
 * @param {number} discount - Discount amount
 * @param {Function} calculateSubtotal - Function to calculate subtotal
 * @param {Function} calculateGST - Function to calculate GST
 * @param {Function} calculateTotal - Function to calculate total
 */
export const formatBillData = (order, discount, calculateSubtotal, calculateGST, calculateTotal) => {
  // Detect AC Hall table (by table label or section if available)
  let isAcHall = false;
  if (order.section) {
    isAcHall = order.section === 'AC Hall';
  } else if (order.tableLabel) {
    isAcHall = order.tableLabel.toLowerCase().includes('ac hall');
  } else if (order.tableNumber && typeof order.tableNumber === 'string') {
    isAcHall = order.tableNumber.toLowerCase().includes('ac hall');
  }
  // Fallback: try to infer from tableNumber if it's a number in AC Hall range (customize if needed)
  // Calculate subtotal and AC Hall charge
  const subtotal = calculateSubtotal();
  const acHallCharge = isAcHall ? subtotal * 0.2 : 0;
  const gst = calculateGST();
  const total = subtotal + gst + acHallCharge - discount;
  return {
    billNumber: order.orderNumber || `BILL-${order.id}-${Date.now()}`,
    date: new Date().toLocaleDateString('en-IN'),
    time: new Date().toLocaleTimeString('en-IN'),
    tableNumber: (order.section ? order.section + ' ' : '') + (order.number || order.tableNumber || order.label),
    waiterName: order.waiterName,
    items: (order.items || []).map(item => ({
      ...item,
      name: item.itemName || item.name || 'Item',
    })),
    subtotal,
    gst,
    acHallCharge,
    discount: discount,
    total,
  };
};

